import React, { useState } from 'react';
import { themes } from './themes';

interface ThemeSelectorProps {
  currentTheme: string;
  onThemeChange: (theme: string) => void;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ currentTheme, onThemeChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleThemeSelect = (themeName: string) => {
    onThemeChange(themeName);
    setIsOpen(false);
  };

  return (
    <div style={styles.container}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={styles.button}
        aria-label="Change color theme"
        aria-expanded={isOpen}
      >
        🎨 Theme
      </button>

      {isOpen && (
        <div style={styles.dropdown} role="menu">
          {Object.entries(themes).map(([key, theme]) => (
            <button
              key={key}
              onClick={() => handleThemeSelect(key)}
              style={{
                ...styles.option,
                ...(currentTheme === key ? styles.optionActive : {}),
              }}
              role="menuitem"
              aria-current={currentTheme === key}
            >
              <div
                style={{
                  ...styles.colorPreview,
                  background: theme.colors.accent,
                }}
              />
              <span style={styles.optionText}>
                {theme.name}
                {currentTheme === key && ' ✓'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 1000,
  },
  button: {
    background: 'var(--button-bg)',
    color: 'var(--button-text)',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 20px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
  },
  dropdown: {
    position: 'absolute',
    top: '60px',
    right: '0',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    minWidth: '220px',
    overflow: 'hidden',
    animation: 'slideDown 0.2s ease',
  },
  option: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    padding: '14px 16px',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: 'var(--text-primary)',
    fontSize: '15px',
    transition: 'background 0.2s ease',
    borderBottom: '1px solid var(--border)',
  },
  optionActive: {
    background: 'var(--accent-light)',
    fontWeight: '600',
  },
  colorPreview: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid var(--border)',
    flexShrink: 0,
  },
  optionText: {
    flex: 1,
  },
};
